import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Beaker, X, ChevronUp, ChevronDown, Shield, Briefcase, Presentation, Home as HomeIcon, FileText, ExternalLink } from "lucide-react";
import { isDemoMode, setDemoMode } from "../lib/links";

/**
 * Floating Demo launcher.
 * Visible only on FMO-facing surfaces (never on consumer pages, so real annuity-review
 * leads can never accidentally see it):
 *   - /demo            (FMO marketing page)
 *   - /pitch           (slide deck)
 *   - /admin/*         (FMO admin dashboards · except /admin/login)
 *   - /agent (and /agent/me/profile) — but NOT /agent/:slug public landing pages
 *
 * Activated when:
 *   - admin or agent is signed in, OR
 *   - URL has ?demo=1 / sessionStorage flag is set
 * Click the badge to expand a quick-jump menu with links to all key surfaces.
 * Click × to exit demo mode globally.
 */
function isFmoOnlyPath(pathname) {
    if (!pathname) return false;
    if (pathname === "/demo") return true;
    if (pathname === "/pitch") return true;
    if (pathname === "/admin/login") return false; // login page should be clean
    if (pathname === "/admin" || pathname.startsWith("/admin/")) return true;
    // Match "/agent" and "/agent/me/profile" but NOT "/agent/:slug" public landing
    if (pathname === "/agent") return true;
    if (pathname.startsWith("/agent/me")) return true;
    return false;
}

export default function DemoBadge() {
    const navigate = useNavigate();
    const location = useLocation();
    const [on, setOn] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOn(isDemoMode());
    }, [location.pathname, location.search]);

    // Keyboard shortcut: Cmd/Ctrl + K toggles the launcher (only when demo mode is on)
    useEffect(() => {
        if (!on) return;
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
            } else if (e.key === "Escape" && open) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [on, open]);

    if (!on) return null;
    if (!isFmoOnlyPath(location.pathname)) return null;

    const exit = () => {
        setDemoMode(false);
        const params = new URLSearchParams(location.search);
        params.delete("demo");
        const qs = params.toString();
        navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
        setOn(false);
        setOpen(false);
    };

    const links = [
        { to: "/", label: "Public Home", icon: HomeIcon, hint: "What real clients see" },
        { to: "/review-demo", label: "Demo Form", icon: FileText, hint: "Autofill scenarios live here · never saves leads" },
        { to: "/admin/login?prefill=admin", label: "Admin Dashboard", icon: Shield, hint: "FMO control center · auto sign-in" },
        { to: "/admin/login?prefill=agent", label: "Agent Dashboard", icon: Briefcase, hint: "What agents see · auto sign-in" },
        { to: "/pitch?presenter=Mark%20Stevens&agent=mark-stevens", label: "Pitch Deck", icon: Presentation, hint: "15-slide presentation" },
    ];

    return (
        <div className="fixed bottom-5 left-5 z-50" data-testid="demo-mode-badge">
            {open && (
                <div className="mb-2 bg-white rounded-xl shadow-2xl border border-amber-200 overflow-hidden w-72 animate-[fadeIn_.15s_ease-out]" data-testid="demo-launcher-panel">
                    <div className="bg-gradient-to-r from-amber-400 to-amber-300 text-slate-900 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Beaker className="w-4 h-4" />
                            <span className="text-[11px] font-bold uppercase tracking-[0.18em]">Demo Quick-Launch</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-mono font-bold bg-slate-900/15 px-1.5 py-0.5 rounded border border-slate-900/20">⌘K</kbd>
                            <button onClick={() => setOpen(false)} className="rounded-full p-1 hover:bg-slate-900/10" data-testid="demo-collapse">
                                <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                    <div className="p-2">
                        {links.map((l) => {
                            const Icon = l.icon;
                            return (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`demo-jump-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="flex items-start gap-3 px-3 py-2.5 rounded-md hover:bg-slate-50 transition-colors group"
                                >
                                    <div className="w-8 h-8 rounded-md bg-slate-100 group-hover:bg-amber-100 text-slate-700 group-hover:text-amber-800 flex items-center justify-center shrink-0 transition-colors">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-slate-900">{l.label}</div>
                                        <div className="text-xs text-slate-700">{l.hint}</div>
                                    </div>
                                    <ExternalLink className="w-3.5 h-3.5 text-slate-600 group-hover:text-amber-600 mt-1.5 transition-colors" />
                                </Link>
                            );
                        })}
                    </div>
                    <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between bg-slate-50">
                        <span className="text-[10px] text-slate-700 uppercase tracking-[0.15em] font-semibold">Demo only · clients never see this</span>
                        <button onClick={exit} className="text-[11px] font-semibold text-rose-600 hover:text-rose-700" data-testid="demo-mode-exit">Exit demo</button>
                    </div>
                </div>
            )}

            <button
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-full shadow-lg pl-3 pr-2 py-1.5 border border-amber-500/50 transition-all"
                data-testid="demo-mode-toggle"
                title="Demo Quick-Launch (⌘K / Ctrl+K)"
            >
                <Beaker className="w-3.5 h-3.5" />
                <span className="text-[11px] font-bold uppercase tracking-[0.18em]">Demo Mode</span>
                <kbd className="hidden sm:inline text-[9px] font-mono font-bold bg-slate-900/15 px-1.5 py-0.5 rounded border border-slate-900/20">⌘K</kbd>
                {open ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </button>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
